import { Info, X, Coffee, Leaf, ShieldAlert, Users, Code } from "lucide-react";
import { t } from "@/lib/i18n";

/**
 * About modal — opens as an overlay over the map (same z-layer as other sheets).
 * Shows app info, legal disclaimer, privacy statement, and community/tech details.
 * The static /about.html remains for SEO crawlers; this modal is the in-app UX.
 */
export interface AboutModalProps {
  onClose: () => void;
}

export function AboutModal({ onClose }: AboutModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center fade-in sm:items-center">
      <button
        type="button"
        aria-label={t("create.cancel")}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-[var(--radius-sheet)] bg-bg-elevated p-5 pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] shadow-2xl sheet-up sm:rounded-2xl"
        role="dialog"
        aria-modal="true"
      >
        <div className="mx-auto mb-3 sheet-grabber sm:hidden" />

        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Info size={22} className="text-accent" />
            <h2 className="text-lg font-bold text-fg">{t("about.title")}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-fg-muted hover:bg-surface"
            aria-label={t("create.cancel")}
          >
            <X size={20} />
          </button>
        </div>

        <p className="mt-1 text-sm text-fg-muted">{t("about.subtitle")}</p>

        {/* Content sections */}
        <div className="mt-4 space-y-4 text-sm text-fg-muted">

          {/* What is Stnr.Space */}
          <Section icon={<Leaf size={15} className="text-accent" />} title={t("about.whatTitle")}>
            <p>{t("about.whatBody")}</p>
          </Section>

          {/* How it works */}
          <Section title={t("about.howTitle")}>
            <p>{t("about.howBody")}</p>
          </Section>

          {/* Legal */}
          <Section icon={<ShieldAlert size={15} className="text-legal-amber" />} title={t("about.legalTitle")}>
            <p>{t("about.legalBody")}</p>
          </Section>

          {/* Privacy */}
          <Section title={t("about.privacyTitle")}>
            <p>{t("about.privacyBody")}</p>
          </Section>

          {/* Community */}
          <Section icon={<Users size={15} className="text-accent" />} title={t("about.communityTitle")}>
            <p>{t("about.communityBody")}</p>
          </Section>

          {/* Tech */}
          <Section icon={<Code size={15} className="text-fg-subtle" />} title={t("about.techTitle")}>
            <p>{t("about.techBody")}</p>
          </Section>

          {/* Support */}
          <div className="rounded-xl bg-surface p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-fg">{t("about.supportTitle")}</p>
                <p className="mt-0.5 text-xs text-fg-subtle">{t("about.supportBody")}</p>
              </div>
              <a
                href="https://ko-fi.com/I2D821LCGP"
                target="_blank"
                rel="noopener noreferrer"
                className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#ff5e5b] px-3 py-2 text-xs font-semibold text-white shadow-lg ring-1 ring-white/10 transition hover:brightness-110 active:scale-95"
              >
                <Coffee size={14} />
                Ko-fi
              </a>
            </div>
          </div>
        </div>

        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full rounded-lg bg-surface py-2.5 text-sm font-medium text-fg ring-1 ring-line transition hover:bg-bg"
        >
          {t("about.close")}
        </button>

        <p className="mt-3 text-center text-xs text-fg-subtle">
          © 2026 Stnr.Space · {t("app.tagline")}
        </p>
      </div>
    </div>
  );
}

/** A titled section with an optional icon, separated by a subtle border. */
function Section({
  icon,
  title,
  children,
}: {
  icon?: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-line pt-3 first:border-0 first:pt-0">
      <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-fg">
        {icon}
        {title}
      </h3>
      {children}
    </div>
  );
}
